import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FeaturedIcon,
  FieldWrapper,
  Icons,
  Label,
  LoadingIndicator,
  Textarea,
  useToggle,
} from '@commenda/kakashi';
import { useState } from 'react';
import { useNotification } from '../../../hooks/useNotification';
import { Filing } from '../../../molecules';

interface ArchiveFilingModalProps {
  isOpen: boolean;
  onClose: () => void;
  filing: Filing;
  onArchive: (filingId: string, reason: string) => Promise<boolean>;
  onSuccess: () => void;
}

export const ArchiveFilingModal = ({
  isOpen,
  onClose,
  filing,
  onArchive,
  onSuccess,
}: ArchiveFilingModalProps) => {
  const { setNotification } = useNotification();
  const [reason, setReason] = useState('');
  const isSubmitting = useToggle(false);

  const handleArchive = async () => {
    isSubmitting.on();
    const success = await onArchive(filing.id, reason);

    if (!success) {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to archive filing.',
      });
      isSubmitting.off();
      return;
    }

    setNotification({
      show: true,
      valid: true,
      title: 'Success',
      message: 'Filing archived successfully.',
    });

    await onSuccess();
    isSubmitting.off();
    onClose();
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogContent
        onClick={(e) => e.stopPropagation()}
        preventCloseOnOutsideClick
      >
        <DialogHeader className="flex flex-col gap-lg items-start border-b-0 pb-0">
          <FeaturedIcon
            size="lg"
            variant="warning"
            icon={<Icons.AlertTriangle />}
          />
          <div className="space-y-xs">
            <DialogTitle>Archive filing?</DialogTitle>
            <DialogDescription>
              Tell us more about why you wish to archive{' '}
              <span className="font-bold">{filing.filingName}</span> for{' '}
              {filing.corporation.companyDisplayName}. Once archived,
              notifications and updates for the filing will be stopped.
            </DialogDescription>
          </div>
        </DialogHeader>
        <div className="p-3xl">
          <FieldWrapper>
            <Label>
              Tell us why
              <span className="text-utility-violet-600 ml-xxs">*</span>
            </Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Type here..."
            />
          </FieldWrapper>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose} size="md">
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleArchive}
            size="md"
            disabled={!reason || isSubmitting.value}
          >
            {isSubmitting.value ? <LoadingIndicator size={16} /> : null}
            <span>Confirm</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
